import type { Brand } from './brands.api';
import type { Category } from './categories.api';

export type UbuyText = {
  mn: string;
  en: string;
};

export type UbuyNavItem = {
  label: UbuyText;
  href: string;
};

export type UbuyStore = {
  code: string;
  label: string;
  subtitle: UbuyText;
  href: string;
  productCount?: number;
};

export type UbuyHeroSlide = {
  eyebrow: UbuyText;
  title: UbuyText;
  body: UbuyText;
  cta: UbuyText;
  href: string;
  tone: 'gold' | 'black' | 'red';
};

export type UbuyMerchandisingSection = {
  id: string;
  title: UbuyText;
  description: UbuyText;
  href: string;
};

export const sourceStores: UbuyStore[] = [
  {
    code: 'KR',
    label: 'Korea',
    subtitle: { mn: 'Солонгос бараа', en: 'Korean goods' },
    href: '/products?store=korea',
  },
  {
    code: 'JP',
    label: 'Japan',
    subtitle: { mn: 'Япон сонголт', en: 'Japan picks' },
    href: '/products?store=japan',
  },
  {
    code: 'US',
    label: 'USA',
    subtitle: { mn: 'Global deals', en: 'Global deals' },
    href: '/products?store=usa',
  },
  {
    code: 'EU',
    label: 'Europe',
    subtitle: { mn: 'Premium brands', en: 'Premium brands' },
    href: '/products?store=europe',
  },
  {
    code: 'CN',
    label: 'China',
    subtitle: { mn: 'Everyday finds', en: 'Everyday finds' },
    href: '/products?store=china',
  },
];

export const fallbackCategories: Category[] = [
  { id: 'fallback-luggage', name: 'Luggage', slug: 'luggage', productCount: 28 },
  { id: 'fallback-men', name: 'Men', slug: 'men', productCount: 3 },
  { id: 'fallback-shoes', name: 'Shoes', slug: 'shoes', productCount: 14 },
  { id: 'fallback-storage', name: 'Storage & Organization', slug: 'storage-organization', productCount: 20 },
  { id: 'fallback-student', name: 'Student Essentials', slug: 'student-essentials', productCount: 21 },
  { id: 'fallback-toys', name: 'Toys', slug: 'toys', productCount: 35 },
  { id: 'fallback-travel', name: 'Travel Accessories', slug: 'travel-accessories', productCount: 25 },
  { id: 'fallback-beauty', name: 'Beauty', slug: 'beauty', productCount: 18 },
  { id: 'fallback-electronics', name: 'Electronics', slug: 'electronics', productCount: 16 },
  { id: 'fallback-home', name: 'Home Goods', slug: 'home-goods', productCount: 22 },
];

export const fallbackBrands: Brand[] = [
  { id: 'fallback-nike', name: 'Nike', slug: 'nike', productCount: 12 },
  { id: 'fallback-jordan', name: 'Jordan', slug: 'jordan', productCount: 8 },
  { id: 'fallback-puma', name: 'Puma', slug: 'puma', productCount: 7 },
  { id: 'fallback-adidas', name: 'Adidas', slug: 'adidas', productCount: 10 },
  { id: 'fallback-amazon', name: 'Amazon', slug: 'amazon', productCount: 18 },
  { id: 'fallback-costco', name: 'Costco', slug: 'costco', productCount: 24 },
  { id: 'fallback-costco-korea', name: 'Costco Korea', slug: 'costco-korea', productCount: 20 },
  { id: 'fallback-samsung', name: 'Samsung', slug: 'samsung', productCount: 11 },
  { id: 'fallback-lg', name: 'LG', slug: 'lg', productCount: 9 },
  { id: 'fallback-olive-young', name: 'Olive Young', slug: 'olive-young', productCount: 15 },
];

export const heroSlides: UbuyHeroSlide[] = [
  {
    eyebrow: { mn: 'KG Goods global shelf', en: 'KG Goods global shelf' },
    title: {
      mn: 'Монголд хэрэгтэй импортын барааг нэг дороос',
      en: 'Imported goods for Mongolia, curated in one store',
    },
    body: {
      mn: 'Хайлт, ангилал, брэнд, deal-ээр хурдан олж, QPay болон cargo салбараар захиална.',
      en: 'Search by product, brand, category, or deal, then keep the same QPay and cargo branch flow.',
    },
    cta: { mn: 'Бараа хайх', en: 'Start shopping' },
    href: '/products',
    tone: 'gold',
  },
  {
    eyebrow: { mn: 'Хүргэлт ба салбар', en: 'Cargo-ready checkout' },
    title: {
      mn: 'Төлбөрөө төлөөд ойр cargo салбараа сонгоно',
      en: 'Pay securely, then choose your cargo branch',
    },
    body: {
      mn: 'Одоогийн Монгол хэрэглэгчийн checkout, QPay QR, банкны app link, салбар сонголт хэвээр.',
      en: 'The local checkout, QPay QR, bank app links, and branch selection stay intact.',
    },
    cta: { mn: 'Салбар харах', en: 'Browse branches' },
    href: '/cargo-branches',
    tone: 'black',
  },
];

export const uspItems = [
  {
    title: { mn: 'QPay төлбөр', en: 'QPay checkout' },
    body: { mn: 'QR код болон банкны апп-аар хялбархан төлнө.', en: 'Pay easily with QR code or your bank app.' },
  },
  {
    title: { mn: 'Cargo салбар', en: 'Cargo branches' },
    body: { mn: 'Төлбөрийн дараа ойрын салбараасаа авна.', en: 'Pick up from the nearest branch after payment.' },
  },
  {
    title: { mn: 'Олон улсын бараа', en: 'Imported goods' },
    body: { mn: 'Гадаадын чанартай бараануудыг KG Goods-оор.', en: 'Quality imported products, curated by KG Goods.' },
  },
  {
    title: { mn: 'Монгол дэмжлэг', en: 'Local support' },
    body: { mn: 'Монгол хэлээр захиалга өгч, лавлагаа авна.', en: 'Order and get help in Mongolian, locally.' },
  },
];

export const merchandisingSections: UbuyMerchandisingSection[] = [
  {
    id: 'latest-deals',
    title: { mn: 'Өнөөдрийн онцлох deal', en: "Today's highlighted deals" },
    description: { mn: 'Үнэ, хямдрал, хурдан авах боломжтой бараа.', en: 'Price-led picks with fast purchase paths.' },
    href: '/products?sale=true',
  },
  {
    id: 'new-launches',
    title: { mn: 'Шинэ ирсэн бараа', en: 'New launches' },
    description: { mn: 'Сүүлийн үеийн шинэ нэмэгдсэн сонголтууд.', en: 'Fresh additions to the KG Goods shelf.' },
    href: '/products?filter=new',
  },
  {
    id: 'cargo-ready',
    title: { mn: 'Cargo-д бэлэн сонголтууд', en: 'Cargo-ready picks' },
    description: { mn: 'Ойрын cargo салбараас авах боломжтой бараанууд.', en: 'Items ready for pickup at your nearest cargo branch.' },
    href: '/cargo-branches',
  },
];

export const topSearches = [
  'Nike shoes',
  'Korean skincare',
  'T-shirt',
  'Coffee',
  'Backpack',
  'Winter jacket',
  'Supplements',
  'Sneakers',
];
